import { useEffect, useRef, useState } from "react";
import { Container, Fish, Fuel, Sailboat, Ship } from "lucide-react";
import { openXPost } from "@/lib/api";

// Category hues, set per theme in index.css: ships and "you" blue (the accent), trades green, money and the Hall gold,
// duels and the Row rose; grey for anything that isn't a category, red for errors.
export const ACC = { blue: "var(--x-accent)", green: "var(--x-pos)", gold: "var(--x-gold)", rose: "var(--x-rose)", grey: "var(--x-grey)", red: "var(--x-neg)" };

// The HUD's glass themes. "auto" follows the system's light or dark; the choice is kept on this device.
export const THEMES = [["auto", "Auto"], ["light", "Light"], ["dark", "Dark"], ["clear", "Clear"], ["tinted", "Tinted"]];
const darkMq = window.matchMedia("(prefers-color-scheme: dark)");
const applyTheme = (t) => { document.documentElement.dataset.theme = t === "auto" ? (darkMq.matches ? "dark" : "light") : t; };
applyTheme(localStorage.getItem("xf-theme") ?? "auto");
export function useTheme() {
  const [theme, setTheme] = useState(() => localStorage.getItem("xf-theme") ?? "auto");
  useEffect(() => {
    applyTheme(theme);
    localStorage.setItem("xf-theme", theme);
    if (theme !== "auto") return;
    const follow = () => applyTheme("auto");
    darkMq.addEventListener("change", follow);
    return () => darkMq.removeEventListener("change", follow);
  }, [theme]);
  return [theme, setTheme];
}
export const SHIP = { rowboat: Sailboat, fishing: Fish, cargo: Container, tanker: Fuel, liner: Ship };
export const STOPS = ["Docks", "Uptown", "The Row", "Mayor"];

export const fmt = (n) => Math.round(n ?? 0).toLocaleString("en-US");
export const today = () => new Date().toISOString().slice(0, 10);
export const reduced = () => window.matchMedia("(prefers-reduced-motion: reduce)").matches;
export const dur = (ms) => {
  if (ms <= 0) return "now";
  const s = Math.floor(ms / 1000), m = Math.floor(s / 60), h = Math.floor(m / 60), d = Math.floor(h / 24);
  const two = (n) => String(n).padStart(2, "0");
  return d ? `${d}d ${h % 24}h` : h ? `${h}h${m % 60 ? ` ${two(m % 60)}m` : ""}` : m >= 10 ? `${m}m` : m ? `${m}m ${two(s % 60)}s` : `${s}s`;
};
export const clock = (iso) => {
  const t = new Date(iso), same = t.toDateString() === new Date().toDateString();
  return t.toLocaleString([], { ...(same ? {} : { weekday: "short" }), hour: "2-digit", minute: "2-digit" });
};

// 0 Docks, 1 Uptown, 2 Millionaires' Row, 3 Mayor
export const stopOf = (econ, me) => (econ.hall.mayor?.k === me?.k ? 3 : me?.mansion ? 2 : econ.uptown ? 1 : 0);

export const tradeBlock = (p, econ, meK) =>
  !econ ? "Sign in to trade" : p.k === meK ? "Your dock" : econ.visits.went.includes(p.k) ? "Traded today" : econ.visits.left <= 0 ? "No trades left" : null;

export function shareDock(me) {
  const text = me?.share?.moment === "uptown"
    ? "My house is uptown in XField. Come trade with me: every trade boosts my ships and opens a mystery crate for you →"
    : "Come trade at my dock in XField. Every trade boosts my ships and opens a mystery crate for you →";
  const url = me?.share?.url ?? window.location.origin;
  if (navigator.share && window.matchMedia("(pointer: coarse)").matches) navigator.share({ text, url }).catch(() => {});
  else openXPost(text, url);
}

// Server time, ticking every second, so timers count down without asking again.
export function useNow(offset = 0) {
  const [t, setT] = useState(() => Date.now() + offset);
  useEffect(() => {
    const tick = () => setT(Date.now() + offset);
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [offset]);
  return t;
}

export function useMedia(query) {
  const [m, setM] = useState(() => window.matchMedia(query).matches);
  useEffect(() => {
    const q = window.matchMedia(query), on = () => setM(q.matches);
    on();
    q.addEventListener("change", on);
    return () => q.removeEventListener("change", on);
  }, [query]);
  return m;
}

export function useOnline() {
  const [on, setOn] = useState(navigator.onLine);
  useEffect(() => {
    const up = () => setOn(true), down = () => setOn(false);
    window.addEventListener("online", up);
    window.addEventListener("offline", down);
    return () => { window.removeEventListener("online", up); window.removeEventListener("offline", down); };
  }, []);
  return on;
}

// Keys ignored while typing: { e: fn, Escape: fn }
export function useHotkeys(map) {
  const ref = useRef(map);
  ref.current = map;
  useEffect(() => {
    const on = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey || e.target.closest?.("input, textarea, select, [contenteditable]")) return;
      const fn = ref.current[e.key.length === 1 ? e.key.toLowerCase() : e.key];
      if (!fn) return;
      e.preventDefault();
      fn();
    };
    window.addEventListener("keydown", on);
    return () => window.removeEventListener("keydown", on);
  }, []);
}
